/**
 * Device diagnostics view (admin-only API): which users have registered
 * devices and whether they can receive calls. Port of the legacy
 * /system/devices screen.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';
import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { httpClient } from '@/utils/httpClient';
import type { ApiEnvelope } from '@/types/api';

interface DeviceInfo {
  platform?: string;
  [key: string]: any;
}

interface DiagnosticRow {
  user_id: number;
  full_name: string;
  email: string;
  role_name: string;
  organization_name: string;
  devices: DeviceInfo[];
  canReceiveCalls: boolean;
}

export const DevicesView = () => {
  const translate = useTranslate();
  const [rows, setRows] = useState<DiagnosticRow[] | null>(null);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    httpClient
      .get<ApiEnvelope<DiagnosticRow[]>>('/users/devicesDiagnostic')
      .then((response) => setRows(Array.isArray(response.data.data) ? response.data.data : []))
      .catch(() => setError(true));
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [row.full_name, row.email, row.organization_name, row.role_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [rows, query]);

  if (error) {
    return (
      <Typography variant="body2" color="error">
        {translate('ra.notification.http_error')}
      </Typography>
    );
  }
  if (!rows) {
    return <CircularProgress size={28} />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        size="small"
        sx={{ maxWidth: 320 }}
        label={translate('common.filters.search', { _: 'Buscar' })}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{translate('pages.system.devices.name')}</TableCell>
            <TableCell>{translate('pages.system.devices.email')}</TableCell>
            <TableCell>{translate('pages.system.devices.role')}</TableCell>
            <TableCell>{translate('pages.system.devices.organization')}</TableCell>
            <TableCell>{translate('pages.system.devices.devices')}</TableCell>
            <TableCell>{translate('pages.system.devices.status')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map((row) => (
            <TableRow key={row.user_id}>
              <TableCell>{row.full_name}</TableCell>
              <TableCell>{row.email}</TableCell>
              <TableCell>{row.role_name}</TableCell>
              <TableCell>{row.organization_name}</TableCell>
              <TableCell>
                {row.devices?.length ? (
                  row.devices.map((device, index) => (
                    <Chip
                      key={index}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                      label={device.platform || translate('pages.system.devices.unknown')}
                    />
                  ))
                ) : (
                  <Chip
                    size="small"
                    color="default"
                    label={translate('pages.system.devices.none')}
                  />
                )}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={row.canReceiveCalls ? 'success' : 'error'}
                  label={
                    row.canReceiveCalls
                      ? translate('pages.system.devices.can_receive')
                      : translate('pages.system.devices.cannot_receive')
                  }
                />
              </TableCell>
            </TableRow>
          ))}
          {!filtered.length && (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography variant="body2" color="text.secondary" align="center">
                  {translate('ra.navigation.no_results')}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export default DevicesView;
